import { useState, useEffect } from 'react'
import { AdminType, Building } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadBuildings, loadAdmins, createTicket, uploadFile } from '../lib/api'
import './CreateTicketForm.css'

interface CreateTicketFormProps {
  onCancel: () => void
  onSuccess: () => void
}

function CreateTicketForm({ onCancel, onSuccess }: CreateTicketFormProps) {
  const { user } = useAuth()
  const [formData, setFormData] = useState({
    adminId: '',
    buildingId: '',
    description: '',
    phone: '',
    file: null as File | null,
  })
  const [buildings, setBuildings] = useState<Building[]>([])
  const [admins, setAdmins] = useState<{ id: string; name: string; admin_type: AdminType }[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadFormData()
  }, [])

  const loadFormData = async () => {
    try {
      setLoading(true)
      const [buildingsData, adminsData] = await Promise.all([
        loadBuildings(),
        loadAdmins(),
      ])
      setBuildings(buildingsData)
      
      // Фильтруем администраторов с admin_type
      const filteredAdmins = adminsData.filter(a => a.admin_type) as { id: string; name: string; admin_type: AdminType }[]
      
      // Если текущий пользователь - IT администратор, ставим его первым в списке
      if (user && user.role === 'admin' && user.admin_type === 'spravce_it') {
        const currentUserAdmin = filteredAdmins.find(a => a.id === user.id)
        if (currentUserAdmin) {
          // Убираем текущего пользователя из списка и ставим первым
          const otherAdmins = filteredAdmins.filter(a => a.id !== user.id)
          setAdmins([currentUserAdmin, ...otherAdmins])
          // Автоматически выбираем текущего пользователя как администратора
          setFormData(prev => ({ ...prev, adminId: user.id }))
        } else {
          setAdmins(filteredAdmins)
        }
      } else {
        setAdmins(filteredAdmins)
      }
    } catch (error) {
      console.error('Chyba při načítání dat:', error)
      alert('Chyba při načítání dat formuláře')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.adminId || !formData.buildingId || !formData.description || !formData.phone || !user) {
      alert('Prosím vyplňte všechna povinná pole')
      return
    }

    setSubmitting(true)
    try {
      let fileUrl: string | undefined = undefined
      let fileName: string | undefined = undefined

      // Загрузка файла, если есть
      if (formData.file) {
        try {
          const uploadResult = await uploadFile(formData.file)
          fileUrl = uploadResult.file_url
          fileName = uploadResult.file_name
        } catch (uploadError: any) {
          console.error('File upload error:', uploadError)
          // Спрашиваем, продолжать ли без файла
          if (!confirm('Chyba při nahrávání souboru: ' + (uploadError.message || 'Neznámá chyba') + '\n\nPokračovat ve vytváření žádosti bez souboru?')) {
            setSubmitting(false)
            return
          }
          // Продолжаем без файла
        }
      }

      await createTicket(
        user.id,
        formData.adminId,
        formData.buildingId,
        formData.description,
        fileName,
        fileUrl,
        formData.phone || undefined
      )

      alert('Žádost byla úspěšně vytvořena')
      onSuccess()
    } catch (error: any) {
      console.error('Chyba při vytváření žádosti:', error)
      alert(error.message || 'Chyba při vytváření žádosti')
    } finally {
      setSubmitting(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] })
    }
  }

  return (
    <div className="create-ticket-form-container">
      <div className="create-ticket-form">
        <h2>Vytvořit novou žádost</h2>
        
        <form onSubmit={handleSubmit}>
          {loading ? (
            <div className="loading">Načítání dat...</div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="adminId">
                  Odpovědný správce <span className="required">*</span>
                </label>
                <select
                  id="adminId"
                  value={formData.adminId}
                  onChange={(e) => setFormData({ ...formData, adminId: e.target.value })}
                  required
                >
                  <option value="">Vyberte správce</option>
                  {admins.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.name} ({admin.admin_type === 'spravce_it' ? 'Správce IT' : 'Správce Budov'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="buildingId">
                  Budova <span className="required">*</span>
                </label>
                <select
                  id="buildingId"
                  value={formData.buildingId}
                  onChange={(e) => setFormData({ ...formData, buildingId: e.target.value })}
                  required
                >
                  <option value="">Vyberte budovu</option>
                  {buildings.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="description">
              Popis problému <span className="required">*</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={5}
              required
              placeholder="Popište svůj problém..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">
              Telefonní číslo <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="+420 123 456 789"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="file">Příloha (volitelné)</label>
            <input
              type="file"
              id="file"
              onChange={handleFileChange}
              accept="image/*,.pdf,.doc,.docx"
            />
            {formData.file && (
              <p className="file-name">Vybraný soubor: {formData.file.name}</p>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-button">
              Zrušit
            </button>
            <button type="submit" disabled={submitting} className="submit-button">
              {submitting ? 'Vytváření...' : 'Vytvořit žádost'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateTicketForm



